import type { TransportMetrics, ClassifyContext } from '../runtime/types';

/**
 * QoS classification thresholds per docs/qos/design-qos.md §QoS Classification.
 *
 * Basis:
 *  - RFC 8836 (RMCAT/GCC): 2% = safe→probing boundary, 10% = probing→decrease
 *  - RTT 200/400ms: empirical for mobile cellular
 *  - Throughput ratio 0.8: Netflix ABR safety margin
 *  - FPS thresholds: 0.5x and 0.8x of nominalFps
 *  - QP > 45 sustained x3 polls = encoder struggling (supplementary signal)
 */

/** Default context when no per-session context is available. */
const DEFAULT_CONTEXT: ClassifyContext = {
  nominalFps: 15,
  hasRtt: true,
  targetBitrateKbps: 0,
  smoothedThroughputKbps: 0,
};

/**
 * How many poll cycles before metrics are considered stale.
 * Stale metrics MUST NOT trigger quality restore (enforced on server too).
 * SDK-side: stale pushed telemetry is classified as 'degraded'.
 * At QOS_POLL_INTERVAL_MS=3000 -> 6s staleness threshold.
 */
const STALE_CYCLES = 2;
const QOS_POLL_INTERVAL_MS = 3000; // keep in sync with session.ts

export class QosController {
  /**
   * Classify transport quality.
   *
   * @param metrics  Current transport metrics (partial fields allowed).
   * @param ctx      Session context; defaults: hasRtt=true, nominalFps=15.
   *
   * Protocol specifics:
   *  - hasRtt=false (JT/T1078): RTT conditions are fully skipped.
   *  - Stale timestampMs: pushed telemetry classified as 'degraded'.
   */
  classify(metrics: TransportMetrics, ctx: ClassifyContext = DEFAULT_CONTEXT): 'good' | 'degraded' | 'poor' {
    const loss = metrics.packetLossRatio ?? 0;
    const rtt = metrics.rttMs ?? 0;
    const fps = metrics.framesPerSecond;
    const qp = metrics.encoderQp;
    const throughput = metrics.availableIncomingBitrateKbps ?? ctx.smoothedThroughputKbps;

    // Freshness check for pushed encoder telemetry (camera_qos / metrics_push).
    // Stale => treat as degraded to prevent spurious quality restore.
    // Authoritative freshness enforcement is on the signaling server.
    if (metrics.timestampMs !== undefined) {
      const ageMs = Date.now() - metrics.timestampMs;
      if (ageMs > STALE_CYCLES * QOS_POLL_INTERVAL_MS) {
        return 'degraded';
      }
    }

    // Poor: any one suffices
    const fpsPoor = fps !== undefined && fps < ctx.nominalFps * 0.5;
    const qpPoor = qp !== undefined && qp > 45;
    const rttPoor = ctx.hasRtt && rtt > 400;

    if (loss > 0.10 || rttPoor || fpsPoor || qpPoor) {
      return 'poor';
    }

    // Degraded: any one suffices
    const fpsDegraded = fps !== undefined && fps < ctx.nominalFps * 0.8;
    const rttDegraded = ctx.hasRtt && rtt > 200;
    const throughputDegraded =
      ctx.targetBitrateKbps > 0 &&
      throughput > 0 &&
      throughput < ctx.targetBitrateKbps * 0.8;

    if (loss > 0.02 || rttDegraded || fpsDegraded || throughputDegraded) {
      return 'degraded';
    }

    return 'good';
  }

  /**
   * Returns true when pushed metrics (timestampMs present) are older than 2 poll cycles.
   * Signaling server must enforce this rule before issuing quality restore.
   */
  isStale(metrics: TransportMetrics): boolean {
    if (metrics.timestampMs === undefined) return false;
    return Date.now() - metrics.timestampMs > STALE_CYCLES * QOS_POLL_INTERVAL_MS;
  }
}
